import React, { useState, useEffect } from 'react';

/**
 * BookSlotModal — shared modal for booking a speaking slot.
 * Collects required title + synopsis before confirming the booking.
 *
 * Props:
 *   meetingDate {string}  YYYY-MM-DD meeting date (for display)
 *   onConfirm   {fn}      (presentation_title, synopsis) => Promise — called on submit
 *   onClose     {fn}      () => void — called on cancel or backdrop click
 */
export default function BookSlotModal({ meetingDate, onConfirm, onClose }) {
  const [title, setTitle] = useState('');
  const [synopsis, setSynopsis] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const formattedDate = meetingDate
    ? new Date(meetingDate).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onConfirm(title.trim(), synopsis.trim());
    } catch (err) {
      setError(err.message || 'Failed to book slot');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--bg-overlay)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-accent)' }}
      >
        <div>
          <h3 className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Claim your slot
          </h3>
          {formattedDate && (
            <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-faint)' }}>
              {formattedDate}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              Title <span style={{ color: 'var(--accent)' }}>*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={300}
              required
              placeholder="e.g. The Art of Persuasion"
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)', outline: 'none' }}
              onFocus={(e) => e.currentTarget.style.borderColor = 'var(--border-accent)'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-accent)'}
            />
            <p
              className="text-xs font-bold mt-1 font-mono"
              style={{
                color: title.length >= 295 ? 'var(--color-danger)' : title.length >= 270 ? 'var(--accent)' : 'var(--text-faint)',
              }}
            >
              {title.length}/300
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              What's it about? <span style={{ color: 'var(--accent)' }}>*</span>
            </label>
            <textarea
              value={synopsis}
              onChange={(e) => setSynopsis(e.target.value)}
              maxLength={1000}
              required
              rows={3}
              placeholder="A sentence or two. What will you say?"
              className="w-full px-3 py-2 rounded-lg text-sm resize-none"
              style={{ background: 'var(--bg-input)', border: '1px solid var(--border-accent)', color: 'var(--text-primary)', outline: 'none' }}
              onFocus={(e) => e.currentTarget.style.borderColor = 'var(--border-accent)'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-accent)'}
            />
            <p
              className="text-xs font-bold mt-1 font-mono"
              style={{
                color: synopsis.length >= 980 ? 'var(--color-danger)' : synopsis.length >= 900 ? 'var(--accent)' : 'var(--text-faint)',
              }}
            >
              {synopsis.length}/1000
            </p>
          </div>

          {error && (
            <p className="text-sm" style={{ color: 'var(--color-danger)' }}>⚠ {error}</p>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="pill-tab text-sm"
              style={{ color: 'var(--text-muted)' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-bouncy text-sm px-5 py-2"
            >
              {loading ? 'Claiming…' : 'Claim this slot'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
